import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";  // Fix WH64 (2026-09-28)
import { toast } from "sonner";
import {
  Building2, Plus, Pencil, Check, X, Loader2, ChevronDown, ChevronUp,
  MapPin, ShieldCheck, AlertCircle,
} from "lucide-react";

const TAX_REGIMES = {
  "601": "General de Ley Personas Morales",
  "603": "Personas Morales con Fines no Lucrativos",
  "612": "Personas Físicas con Actividades Empresariales y Profesionales",
  "626": "Régimen Simplificado de Confianza",
};

const EMPTY_FORM = {
  rfc: "",
  legal_name: "",
  tax_regime: "601",
  address: { street: "", exterior_number: "", neighborhood: "", city: "", state: "", zip_code: "" },
  fiscal_email: "",
  default_payment_terms_days: 15,
  default_iva_rate: 16.0,
  active: true,
};

function StatusBadge({ status, label }) {
  const ok = status === "loaded";
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
      ok ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-700 border border-amber-200"
    }`}>
      {ok ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
      {label}
    </span>
  );
}

function EntityCard({ entity, branches, otherEntities, unassignedBranches, onEdit, onAssign, expanded, onToggle }) {
  const [assigning, setAssigning] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAssign = async (branchId, entityId) => {
    setSaving(true);
    try {
      await api.post(`/ops/branches/${branchId}/assign-legal-entity`, { legal_entity_id: entityId });
      toast.success("Sucursal reasignada");
      onAssign();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reasignar");
    } finally {
      setSaving(false);
      setAssigning(null);
    }
  };

  const isPlaceholder = entity.rfc?.startsWith("PLACEHOLDER");

  return (
    <div
      className={`bg-white rounded-xl border ${entity.active ? "border-slate-200" : "border-slate-100 opacity-60"} overflow-hidden`}
      data-testid={`legal-entity-card-${entity.id}`}
    >
      <div
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
      >
        <div className="p-2 rounded-lg bg-slate-100">
          <Building2 className="w-5 h-5 text-slate-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 truncate">{entity.legal_name}</div>
          <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
            <span className={isPlaceholder ? "text-amber-600 font-medium" : ""}>
              RFC: {entity.rfc}
            </span>
            <span>·</span>
            <span>Regimen {entity.tax_regime}</span>
            <span>·</span>
            <span>{branches.length} {branches.length === 1 ? "sucursal" : "sucursales"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={entity.ciec_status} label="CIEC" />
          <StatusBadge status={entity.csd_status} label="CSD" />
          <button
            onClick={e => { e.stopPropagation(); onEdit(entity); }}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
            data-testid={`edit-legal-entity-btn-${entity.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-4 pb-4 pt-3 space-y-4">
          {isPlaceholder && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>RFC pendiente de completar. Usa el boton de editar para ingresar el RFC real.</span>
            </div>
          )}

          {/* Datos fiscales */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <div><span className="text-slate-500">Email fiscal:</span> <span className="font-medium">{entity.fiscal_email || "—"}</span></div>
            <div><span className="text-slate-500">Plazo de pago:</span> <span className="font-medium">{entity.default_payment_terms_days} dias</span></div>
            <div><span className="text-slate-500">Tasa IVA:</span> <span className="font-medium">{entity.default_iva_rate}%</span></div>
            <div><span className="text-slate-500">Regimen:</span> <span className="font-medium">{TAX_REGIMES[entity.tax_regime] || entity.tax_regime}</span></div>
          </div>

          {/* Dirección */}
          {entity.address?.city && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <MapPin className="w-3.5 h-3.5" />
              <span>{[entity.address.street, entity.address.exterior_number, entity.address.neighborhood, entity.address.city, entity.address.state].filter(Boolean).join(", ")}</span>
            </div>
          )}

          {/* Sucursales asociadas */}
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-2">Sucursales vinculadas</div>
            {branches.length === 0 ? (
              <p className="text-xs text-slate-400">Sin sucursales asignadas</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {branches.map(b => (
                  <span key={b.id} className="inline-flex items-center gap-1.5 text-xs bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full border border-slate-200">
                    <Building2 className="w-3 h-3" />
                    {b.name}
                    {assigning === b.id && (
                      <select
                        className="ml-1 border rounded px-1 py-0.5 text-xs"
                        defaultValue={entity.id}
                        disabled={saving}
                        onChange={e => { if (e.target.value !== entity.id) handleAssign(b.id, e.target.value); }}
                      >
                        <option value={entity.id}>{entity.legal_name}</option>
                        {otherEntities.map(oe => (
                          <option key={oe.id} value={oe.id}>{oe.legal_name}</option>
                        ))}
                      </select>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Acciones sobre sucursales — pull de otras entidades */}
          {unassignedBranches.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-2">Reasignar sucursal a esta entidad</div>
              <div className="flex flex-wrap gap-2" data-testid="branch-assign-section">
                {unassignedBranches.map(b => (
                    <button
                      key={b.id}
                      onClick={() => !saving && handleAssign(b.id, entity.id)}
                      disabled={saving}
                      className="text-xs px-3 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-[#FFDF00] hover:text-slate-800 transition-colors disabled:opacity-50"
                      data-testid={`assign-branch-btn-${b.id}`}
                    >
                      {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                      + {b.name}
                    </button>
                  ))}
                {unassignedBranches.length === 0 && (
                  <p className="text-xs text-slate-400">Todas las sucursales ya estan asignadas a esta entidad</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EntityFormModal({ entity, onClose, onSaved }) {
  const isNew = !entity?.id;
  const [form, setForm] = useState(isNew ? EMPTY_FORM : {
    rfc: entity.rfc || "",
    legal_name: entity.legal_name || "",
    tax_regime: entity.tax_regime || "601",
    address: entity.address || EMPTY_FORM.address,
    fiscal_email: entity.fiscal_email || "",
    default_payment_terms_days: entity.default_payment_terms_days ?? 15,
    default_iva_rate: entity.default_iva_rate ?? 16.0,
    active: entity.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const upd = (field, val) => setForm(p => ({ ...p, [field]: val }));
  const updAddr = (field, val) => setForm(p => ({ ...p, address: { ...p.address, [field]: val } }));

  const handleSave = async () => {
    if (!form.legal_name.trim()) { toast.error("La razon social es obligatoria"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/ops/legal-entities", form);
        toast.success("Entidad legal creada");
      } else {
        await api.put(`/ops/legal-entities/${entity.id}`, form);
        toast.success("Entidad legal actualizada");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h2 className="font-bold text-slate-900">{isNew ? "Nueva entidad legal" : "Editar entidad legal"}</h2>
            <p className="text-xs text-slate-500 mt-0.5">Los RFCs placeholder deben actualizarse con los datos reales del cliente</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Razon social *</label>
            <input
              value={form.legal_name}
              onChange={e => upd("legal_name", e.target.value)}
              placeholder="GRUPO COMERCIAL GALO, S.A. de C.V."
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              data-testid="legal-entity-name-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">RFC</label>
              <input
                value={form.rfc}
                onChange={e => upd("rfc", e.target.value.toUpperCase())}
                placeholder="GCG241001ABC"
                maxLength={20}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                data-testid="legal-entity-rfc-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Regimen fiscal</label>
              <select
                value={form.tax_regime}
                onChange={e => upd("tax_regime", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              >
                {Object.entries(TAX_REGIMES).map(([code, label]) => (
                  <option key={code} value={code}>{code} — {label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Email fiscal</label>
            <input
              type="email"
              value={form.fiscal_email}
              onChange={e => upd("fiscal_email", e.target.value)}
              placeholder="facturacion@empresa.mx"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Plazo de pago (dias)</label>
              <input
                type="number"
                value={form.default_payment_terms_days}
                onChange={e => upd("default_payment_terms_days", parseInt(e.target.value) || 0)}
                min={0} max={365}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Tasa IVA (%)</label>
              <input
                type="number"
                value={form.default_iva_rate}
                onChange={e => upd("default_iva_rate", parseFloat(e.target.value) || 0)}
                min={0} max={100} step={0.5}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              />
            </div>
          </div>

          {/* Dirección */}
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-2">Domicilio fiscal (opcional)</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["street", "Calle"],
                ["exterior_number", "Num. exterior"],
                ["neighborhood", "Colonia"],
                ["city", "Ciudad"],
                ["state", "Estado"],
                ["zip_code", "CP"],
              ].map(([f, label]) => (
                <input
                  key={f}
                  value={form.address[f] || ""}
                  onChange={e => updAddr(f, e.target.value)}
                  placeholder={label}
                  className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                />
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.active}
              onChange={e => upd("active", e.target.checked)}
              className="w-4 h-4 accent-[#FFDF00]"
            />
            <span className="text-sm text-slate-700">Entidad activa</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FFDF00] text-sm font-semibold text-slate-900 hover:bg-yellow-300 disabled:opacity-60 transition-colors"
            data-testid="save-legal-entity-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {isNew ? "Crear entidad" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OpsLegalEntidades() {
  // Fix WH64 (2026-09-28): Legal entra a esta pantalla para dar de alta y
  // editar razones sociales. Reasignar sucursales sigue siendo de Corporativo
  // (el endpoint assign-legal-entity responde 403 a los demás).
  const { user } = useAuth();
  const puedeAsignar = user?.role === "corporate_admin";
  const [entities, setEntities] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entRes, brRes] = await Promise.all([
        api.get("/ops/legal-entities?active_only=false"),
        api.get("/ops/branches"),
      ]);
      setEntities(entRes.data);
      setAllBranches(brRes.data);
    } catch {
      toast.error("Error al cargar entidades legales");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getBranches = (entityId) =>
    allBranches.filter(b => b.legal_entity_id === entityId);

  const toggleExpand = (id) =>
    setExpanded(p => ({ ...p, [id]: !p[id] }));

  return (
    <div className="p-6 space-y-5" data-testid="legal-entities-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Razones sociales</h1>
          <p className="text-sm text-slate-500">Entidades legales del grupo — estructura para CFDI y reportes fiscales</p>
        </div>
        <button
          onClick={() => setEditing({})}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FFDF00] text-sm font-semibold text-slate-900 hover:bg-yellow-300 transition-colors"
          data-testid="new-legal-entity-btn"
        >
          <Plus className="w-4 h-4" />
          Nueva entidad
        </button>
      </div>

      {/* Info del mapeo inicial */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800">
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          El mapeo inicial es provisional: Puerto Vallarta (Olas Altas) → San Blas de Nayarit; el resto → Grupo Galo.
          Confirmar con el cliente y actualizar RFCs antes de usar para reportes fiscales.
        </span>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <div key={i} className="bg-white rounded-xl border border-slate-200 h-20 animate-pulse" />)}
        </div>
      ) : entities.length === 0 ? (
        <div className="text-center py-16">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Sin entidades legales registradas</p>
        </div>
      ) : (
        <div className="space-y-3">
          {entities.map(e => (
            <EntityCard
              key={e.id}
              entity={e}
              branches={getBranches(e.id)}
              otherEntities={entities.filter(ae => ae.id !== e.id).map(ae => ({
                id: ae.id, legal_name: ae.legal_name,
              }))}
              unassignedBranches={puedeAsignar ? allBranches.filter(b => b.legal_entity_id !== e.id) : []}
              onEdit={setEditing}
              onAssign={load}
              expanded={!!expanded[e.id]}
              onToggle={() => toggleExpand(e.id)}
            />
          ))}
        </div>
      )}

      {/* Sucursales sin entidad legal asignada */}
      {!loading && allBranches.some(b => !b.legal_entity_id) && (
        <div className="border border-amber-200 rounded-xl p-4 bg-amber-50">
          <div className="text-xs font-semibold text-amber-800 mb-2">Sucursales sin entidad legal asignada</div>
          <div className="flex flex-wrap gap-2">
            {allBranches.filter(b => !b.legal_entity_id).map(b => (
              <span key={b.id} className="text-xs bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full border border-amber-200">
                {b.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {editing !== null && (
        <EntityFormModal
          entity={Object.keys(editing).length === 0 ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
